import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { CheckinFlowModal } from '../components/CheckinFlowModal';
import { GiftCardModal } from '../components/GiftCardModal';
import { parseGiftCardScan } from '../lib/giftcard';

const SCANNER_REGION_ID = 'qr-scanner-region';

export function ScanScreen() {
  // Token currently being processed by CheckinFlowModal. While set, the
  // scanner is paused so duplicate frames don't fire onDecode again.
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  // A physical gift card (its QR is https://neoseum.com/c/<CODE>) opens the
  // card instead of the check-in flow.
  const [pendingGiftCard, setPendingGiftCard] = useState<string | null>(null);
  const [scannerError, setScannerError] = useState<string | null>(null);
  // Manual entry fallback for when the camera can't read the QR (small print,
  // glare, low-res phone screens). Operator types the code from the email.
  const [manualCode, setManualCode] = useState('');
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const inFlightRef = useRef<string | null>(null);

  // Start the camera on mount; stop on unmount.
  useEffect(() => {
    let cancelled = false;
    // Track the start() promise so cleanup can await it. Without this,
    // unmount-during-start races: start() resolves AFTER cleanup ran →
    // orphan scanner, AND clear() fires before stop() resolves →
    // "Cannot clear while scan is ongoing" (the error showing on Mac).
    // html5-qrcode's start() resolves to `null`, not void — `unknown` since
    // we only care that it settled, not what it produced.
    let startPromise: Promise<unknown> | undefined;

    const scanner = new Html5Qrcode(SCANNER_REGION_ID, {
      verbose: false,
      formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
      // Use the browser's native BarcodeDetector API when available
      // (Android Chrome, recent Edge, Safari 18+). Significantly faster and
      // more accurate than html5-qrcode's WASM fallback — picks up small /
      // low-density QR codes (e.g. the small Bokun ones in confirmation
      // emails) where the WASM decoder gives up.
      experimentalFeatures: {
        useBarCodeDetectorIfSupported: true,
      },
    });
    scannerRef.current = scanner;

    const onDecode = (decoded: string) => {
      if (cancelled) return;
      // Dedupe while a request is in flight.
      if (inFlightRef.current === decoded) return;
      inFlightRef.current = decoded;

      // Pause the scanner so subsequent video frames containing the SAME
      // QR code don't fire onDecode again while the modal is open.
      const sc = scannerRef.current;
      if (sc?.isScanning) {
        try { sc.pause(true); } catch { /* swallow; pause is best-effort */ }
      }

      const giftCode = parseGiftCardScan(decoded);
      if (giftCode) setPendingGiftCard(giftCode);
      else setPendingToken(decoded);
    };

    Html5Qrcode.getCameras()
      .then((cams) => {
        if (cancelled || cams.length === 0) {
          if (!cams.length) setScannerError('Geen camera gevonden.');
          return;
        }
        const back =
          cams.find((c) => /back|rear|environment/i.test(c.label)) ?? cams[cams.length - 1];
        startPromise = scanner.start(
          back!.id,
          {
            fps: 10,
            // Dynamic qrbox sized to ~75% of the viewfinder's smallest edge.
            // Bigger box = decoder samples more of the frame, helps when
            // the customer's printed/email QR is small or held off-centre.
            qrbox: (vw, vh) => {
              const edge = Math.floor(Math.min(vw, vh) * 0.75);
              return { width: edge, height: edge };
            },
            aspectRatio: 1.0,
          },
          (text) => onDecode(text),
          () => {
            // Per-frame failure callback — ignore; this fires every frame
            // that doesn't contain a code.
          },
        );
        return startPromise;
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setScannerError(e instanceof Error ? e.message : String(e));
      });

    return () => {
      cancelled = true;
      const sc = scannerRef.current;
      scannerRef.current = null;
      if (!sc) return;

      // Wait for start() to finish (if still pending), THEN stop, THEN clear.
      // Each step is wrapped — html5-qrcode throws state errors that we don't
      // care about during teardown. Cleanup is fire-and-forget but ordered.
      void (async () => {
        try { await startPromise; } catch { /* start may have rejected; ignore */ }
        try { if (sc.isScanning) await sc.stop(); } catch { /* ignore */ }
        try { sc.clear(); } catch { /* ignore */ }
      })();
    };
  }, []);

  const handleModalClose = () => {
    setPendingToken(null);
    setPendingGiftCard(null);
    inFlightRef.current = null;
    setManualCode('');
    const sc = scannerRef.current;
    if (sc) {
      try { sc.resume(); } catch { /* ignore */ }
    }
  };

  // Submit the manually-typed code through the same path as a camera scan.
  // Pause the scanner so we don't double-fire if the camera also picks it
  // up while the modal is opening.
  const submitManual = (raw: string) => {
    const code = raw.trim();
    if (code.length === 0) return;
    if (inFlightRef.current === code) return;
    inFlightRef.current = code;
    const sc = scannerRef.current;
    if (sc?.isScanning) {
      try { sc.pause(true); } catch { /* ignore */ }
    }
    const giftCode = parseGiftCardScan(code);
    if (giftCode) setPendingGiftCard(giftCode);
    else setPendingToken(code);
  };

  return (
    <div className="screen scan-screen">
      <div id={SCANNER_REGION_ID} className="scan-viewfinder" />

      {scannerError ? <div className="error-card">{scannerError}</div> : null}

      <div className="scan-overlay">
        {pendingToken == null && pendingGiftCard == null ? (
          <div className="scan-hint">
            Richt op de QR-code uit de bevestigingsmail of van een cadeaubon.
          </div>
        ) : null}
      </div>

      {pendingToken == null && pendingGiftCard == null ? (
        <form
          className="scan-manual"
          onSubmit={(e) => {
            e.preventDefault();
            submitManual(manualCode);
          }}
        >
          <label className="scan-manual-label">
            Of typ de code uit de mail of van de cadeaubon
          </label>
          <div className="scan-manual-row">
            <input
              type="text"
              className="scan-manual-input"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              placeholder="bijv. NEO-T129494728"
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
            />
            <button
              type="submit"
              className="walkin-primary-btn scan-manual-submit"
              disabled={manualCode.trim().length === 0}
            >
              Inchecken
            </button>
          </div>
        </form>
      ) : null}

      {pendingToken != null ? (
        <CheckinFlowModal
          qrToken={pendingToken}
          onClose={handleModalClose}
        />
      ) : null}

      {pendingGiftCard != null ? (
        <GiftCardModal code={pendingGiftCard} onClose={handleModalClose} />
      ) : null}
    </div>
  );
}
